// Wake-up session check.
//
// When a console tab sits idle for a long time and the JWT has since expired, the
// operator used to return to a UI where every click silently did nothing until a
// manual reload (the next API call 401s, but nothing prompts one). This watcher
// turns that into an immediate, clean login screen: whenever the tab becomes
// visible again (or the window regains focus), it silently pings the cheapest
// authenticated endpoint (GET /me). If the session is gone, the fetch layer's 401
// policy (libs/authGuard: re-check, then sign out only for a session that really
// ended) lands the operator on a clean login screen; if the session is still valid
// the ping is a no-op the user never notices.
//
// Debounced so a burst of visibility/focus events (or quick tab flips) triggers at
// most one probe per MIN_INTERVAL_MS.

import { isPublicPath } from '@/libs/publicRoutes'
import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'

// Minimum spacing between probes. Short idle flips (Alt-Tab, notifications) must
// not spam /me; a real "came back after a while" is what we care about.
const MIN_INTERVAL_MS = 30_000

let installed = false

export function installSessionWatch(): void {
    // Guard: only ever wire the listeners once for the app's lifetime.
    if (installed) return
    installed = true

    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    let lastCheck = 0

    const probe = () => {
        // Only meaningful when we believe we are logged in and the tab is visible.
        if (!appStore.isLoggedIn()) return
        if (document.visibilityState !== 'visible') return

        // Never probe from the public pages (/login, /signup, /signup/confirm,
        // /downloads): they are served to anonymous visitors by design, and a
        // signed-in operator browsing them has no reason to be bounced to /login.
        if (isPublicPath()) return

        const now = Date.now()
        if (now - lastCheck < MIN_INTERVAL_MS) return
        lastCheck = now

        // Fire-and-forget: a 401 is handled centrally by the fetch layer (hard
        // redirect to /login). Any other outcome is ignored on purpose.
        void (async () => {
            try {
                await fetchStore.fetch('/me').get().json()
            } catch {
                // ignore — the interceptor already handled auth failures
            }
        })()
    }

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') probe()
    })
    window.addEventListener('focus', probe)
}
